import iconsPng from '@/assets/img/png/icons';
import HeroBackgroundPage from '@/components/templates/HeroBackgroundPage';
import TurnstileWidget from '@/components/molecules/TurnstileWidget';
import { CREATOR_APPLICATION_BACKGROUND, CREATOR_APPLICATION_BACKGROUND_OPACITY } from '@/constants/creatorApplication';
import { EMAIL_REGEX, NAME_REGEX } from '@/constants/stringVars';
import useStartContentCreatorApplication from '@/hooks/contentcreator/useStartContentCreatorApplication';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControl, Image, Input, Text, VStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { extra } from '@/theme/colors/palettes';
import CustomButton from '@/components/atoms/CustomButton';
import svgIcons from '@/assets/svg/icons';

type FormData = {
  email: string;
  firstName: string;
  lastName: string;
};

const schema = yup
  .object({
    email: yup.string().required('Email is required.').matches(EMAIL_REGEX, 'Please enter valid email address.'),
    firstName: yup.string().required('First name is required.').matches(NAME_REGEX, 'Please enter valid first name.'),
    lastName: yup.string().required('Last name is required.').matches(NAME_REGEX, 'Please enter valid last name.'),
  })
  .required();

const CreatorApplyFormHero = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const { mutate: startContentCreatorApplication, isPending } = useStartContentCreatorApplication(() => {
    setIsSubmitted(true);
  });
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
  });

  if (isSubmitted) {
    return (
      <VStack
        minHeight={'100vh'}
        width={'100vw'}
        justifyContent={{ base: 'center', md: 'flex-start' }}
        paddingTop={'100px'}
        paddingX={'24px'}
      >
        <Image alt="ok-hand" src={iconsPng.okHand} width={100} height={100} />

        <Text
          variant={'loraTitle'}
          color={'white'}
          fontSize={'26px'}
          lineHeight={'32px'}
          fontWeight={'400'}
          marginTop={'50px'}
        >
          Check your Email
        </Text>

        <Text
          color={'white'}
          fontSize={'16px'}
          fontWeight={'500'}
          lineHeight={'21px'}
          textAlign={'center'}
          maxWidth={'720px'}
          marginTop={'20px'}
        >
          Check your email for a link to complete your application.
        </Text>

        <CustomButton
          isTransparent
          label="Go Back"
          iconBeforeLabelSvgPath={svgIcons.leftArrow}
          iconBeforeLabelSvgBoxHeight={13}
          labelColor={`${extra.white}80`}
          style={{ marginTop: '50px' }}
          iconBeforeLabelSvgColor={`${extra.white}80`}
          hoverOpacityDrop
          onClick={() => setIsSubmitted(false)}
        />
      </VStack>
    );
  }

  return (
    <VStack
      minHeight={'100vh'}
      width={'100vw'}
      justifyContent={{ base: 'center', md: 'flex-start' }}
      paddingTop={{ base: '96px', md: '40px' }}
      paddingX={'24px'}
    >
      <Text
        variant={'loraTitle'}
        color={'white'}
        fontSize={'26px'}
        lineHeight={'32px'}
        fontWeight={'400'}
        marginTop={'100px'}
      >
        Apply to Become a Creator
      </Text>

      <Text
        color={'white'}
        fontSize={{ base: '11px', md: '14px' }}
        fontWeight={'500'}
        lineHeight={{ base: '14px', md: '18px' }}
        textAlign={'center'}
        marginTop={'7px'}
      >
        These are the basic information needed to apply.
      </Text>

      <VStack
        as={'form'}
        width={'100%'}
        maxWidth={'360px'}
        spacing={{ base: '13px', md: '17px' }}
        marginTop={'50px'}
        onSubmit={handleSubmit((data) => {
          if (!turnstileToken || isPending) return;

          startContentCreatorApplication(
            { data, turnstileToken },
            {
              onSettled: () => {
                setTurnstileToken(null);
                setTurnstileKey((key) => key + 1);
              },
            },
          );
        })}
      >
        <FormControl>
          <Input className="custom-input" placeholder={'Email Address'} type={'email'} {...register('email')} />
          <Text variant={'errorYellow'}>{errors.email?.message ?? ''}</Text>
        </FormControl>
        <FormControl>
          <Input className="custom-input" placeholder={'First Name'} {...register('firstName')} />
          <Text variant={'errorYellow'}>{errors.firstName?.message ?? ''}</Text>
        </FormControl>
        <FormControl>
          <Input className="custom-input" placeholder={'Last Name'} {...register('lastName')} />
          <Text variant={'errorYellow'}>{errors.lastName?.message ?? ''}</Text>
        </FormControl>

        <TurnstileWidget
          key={turnstileKey}
          width="360px"
          siteKey={import.meta.env.VITE_CONTACT_SUPPORT_TURNSTILE_SITE_KEY}
          turnstileToken={turnstileToken}
          setTurnstileToken={setTurnstileToken}
        />

        <Button
          variant={'continue'}
          width={'100%'}
          maxWidth={'360px'}
          minWidth={'360px'}
          marginTop={{ base: '23px', md: '35px' }}
          type={'submit'}
          isLoading={isPending}
          isDisabled={!isValid || isPending || !turnstileToken}
          disabled={!isValid || isPending || !turnstileToken}
        >
          Continue
        </Button>
      </VStack>
    </VStack>
  );
};

const CreatorApplyForm = () => (
  <HeroBackgroundPage
    heroChildren={<CreatorApplyFormHero />}
    backgroundOverlay={CREATOR_APPLICATION_BACKGROUND}
    backgroundOverlayOpacity={CREATOR_APPLICATION_BACKGROUND_OPACITY}
  />
);

export const Route = createFileRoute('/creator/apply/form/')({
  staticData: { title: 'Creator Application' },
  component: CreatorApplyForm,
});
